import moment from 'moment';
import axios from 'axios';
import nookies from 'nookies';
import gql from 'graphql-tag';

import Config from './Config';
import banks from './banks';
import { getNodeEnv } from './env';
import { currentLocale, __ } from './I18n';
// eslint-disable-next-line import/no-cycle
import { getApolloClient } from './apollo';

/**
 * @typedef {'AMOUNT' | 'PERCENTAGE'} AmountType
 * @typedef {'BOLETO' | 'CARD' | 'EXTERNAL' | 'PIX'} PaymentMethodType
 * @typedef {'PAID' | 'PROCESSING' | 'OVERDUE' | 'CANCELED' | 'SCHEDULED'} InvoiceStatusType
 * @typedef {'PAID' | 'PROCESSING' | 'PENDING' | 'CANCELED' | 'FAILED'} PaymentStatusType
 * @typedef {'ADMIN' | 'EDIT' | 'VIEW'} PermissionType
 * @typedef {{
 *  id: number
 *  fullname: string
 *  taxId?: string
 *  confRecurrence: boolean
 *  type: string
 *  logo: { uri?: string }
 *  permissions: {
 *    hasAdmin: boolean
 *    entityScopes: string[]
 *  }
 *  recipientTags: { totalCount: number }
 *  accounts: { totalCount: number }
 *  myEntities: { totalCount: number }
 *  collectionRule: { totalCount: number }
 * }} FetchedOrganizationFromUser
 */

/**
 * @param {{ name: string }[]} arr
 * @returns {string}
 */
export const arrayToString = (arr, separator = ', ', empty = '-') => {
  let rolesString = '';
  arr.forEach((role, i) => {
    if (i + 1 === arr.length) rolesString = rolesString.concat(`${role.name}`);
    else rolesString = rolesString.concat(`${role.name}${separator}`);
  });
  return rolesString || empty;
};

/**
 * @param {{
 *  numerator: number,
 *  denominator: number,
 *  number: number?,
 *  fractionDigits: number
 * }} params
 * @returns {string}
 */
export const percent = ({
  numerator, denominator, number = null, fractionDigits = 2
}) => {
  if ((number !== null && typeof number !== 'number') && !denominator) return '-';

  const value = number !== null ? number : (numerator / denominator);

  return (value * 100).toLocaleString(currentLocale(), {
    minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits
  }) + '%';
};

/**
 * @param {Date | string} date
 * @param {boolean} [time]
 * @param {string} [format]
 * @returns {string}
 */
export const simpleDate = (date, time = false, format = '', locale = 'pt') => {
  const now = moment();
  moment.locale(locale);
  let then = moment(date);
  const diff = then.diff(now, 'hours');

  then = then.utcOffset(now.utcOffset());

  if (format) {
    return then.format(format);
  }

  if (diff > -12 && diff < 12 && time) {
    return then.format('LT');
  } if (diff > -4380 && diff < 4380) { //24 * 365/2
    return then.format('MMM D');
  }

  return then.format('L');
};

export const classAppInstance = () => axios.create({
  //@ts-ignore
  baseURL: `${Config.api.uri[getNodeEnv()]}`
});

/**
 * @param {string} path
 * @returns {string}
 */
export const asset = path => Config.assets[getNodeEnv()] + path;

/**
 * @param {string | number} value
 * @param {string} [currency]
 * @param {number} [fractionDigits]
 * @returns {string}
 */
export const currencyMask = (value, currency = 'BRL', fractionDigits = 2) => {
  //@ts-ignore - TODO check parseFloat expects 1 arguments
  const str = parseFloat(value, 10).toFixed(fractionDigits);
  //@ts-ignore
  const prefix = Config.currency[currency].label;
  //@ts-ignore
  const decimalSep = Config.currency[currency].decimalSeparator;
  //@ts-ignore
  const thousandSep = Config.currency[currency].thousandSeparator;

  const splittedString = str.split('.');

  if (splittedString.length === 1) return `${prefix} ${str}${decimalSep}00`;
  if (splittedString[1].length === 1) return `${prefix} ${splittedString[0]}${decimalSep}${splittedString[1]}0`;
  const isNegative = parseFloat(splittedString[0]) < 0;
  //@ts-ignore - TODO fix O lado esquerdo de uma operação aritmética deve ser do tipo 'any', 'number', 'bigint' ou um tipo enumerado.
  const prepareTotal = String(isNegative ? (splittedString[0] * -1) : splittedString[0]).split(/(?=(?:...)*$)/).join(thousandSep);

  const response = `${prefix} ${isNegative ? '-' : ''}${prepareTotal}${decimalSep}${splittedString[1]}`;
  return response;
};

/**
 * @param {string} str
 * @returns {string}
 */
export const phoneMask = (str) => {
  const code = str.slice(2, 4);
  const start = str.slice(4, str.length - 4);
  const end = str.slice(str.length - 4);
  return `(${code}) ${start}-${end}`;
};

/**
 * @param {string | Date} date
 * @returns {string}
 */
export const formatDate = (date) => {
  const formated = moment(date).format();
  return (`${formated.slice(8, 10)}/${formated.slice(5, 7)}/${formated.slice(0, 4)}`);
};

/**
 * TODO check if function is still in use
 * @param {string} type
 * @param {string[]} headers
 * @param {Array<object>} data
 * @param {(agr: Array<object>) => {line: string}[]} [formatFn]
 * @returns {string}
 */
export const makeFile = (type, headers, data, formatFn) => {
  let file = '';

  if (headers.length) {
    file += headers.join(';') + '\n';
  }

  if (data.length) {
    const rows = formatFn ? formatFn(data) : data;
    rows.forEach((line, i) => {
      //@ts-ignore - Fix join() type error
      file += line.join(';');

      if (i < data.length - 1) {
        file += '\n';
      }
    });
  }

  return `data:${type};charset=utf-8,${encodeURIComponent(file)}`;
};

/**
 * @param {string} organizationType
 * @returns {boolean}
 */
export const organizationIsSchool = (organizationType) => !!organizationType
  ?.match(/school|curricular|ballet|course|education|university|soccer|(^de$)/i);

/**
 * @param {*} context
 * @param {string} name
 * @param {string} value
 * @param {number} [maxAge] Time in seconds
 * @param {string} [domain]
 */
export const setCookie = (context, name, value, maxAge = 7 * 24 * 60 * 60, domain = undefined) => {
  nookies.destroy(context, name);
  nookies.set(context, name, value, { maxAge, path: '/', domain });
};

/**
 * @param {*} context
 * @param {string} name
 * @param {{ path: string, domain: string }} [options]
 */
export const destroyCookie = (context, name, options) => {
  nookies.destroy(context || {}, name, options);
};

/**
 * @param {*} context
 * @param {string} name
 */
export const getCookie = (context, name) => nookies.get(context)[name] || null;

/**
 * @param {*} params
 * @param {string} accessToken
 */
const setParams = (params = {}, accessToken) => ({
  tz_offset: -(new Date().getTimezoneOffset()),
  locale: currentLocale(),
  ...params,
  access_token: accessToken,
  client_id: Config.authClientId
});

/**
 * @param {string} type
 * @param {(id: string | number, arg2: [number]) => string} query
 * @param {string[] | number[]} data
 * @param {*} pieces
 * @returns {Promise<any>}
 */
export const apiPatch = (type, query, data, pieces = 20) => {
  //@ts-ignore
  const blocks = data.reduce((prev, item, i) => {
    const where = Math.floor(i / pieces);
    const isNew = i === 0 || i % pieces === 0;
    if (isNew) {
      prev.push([query(item, i)]);
    } else {
      prev[where].push(query(item, i));
    }
    return prev;
  }, []);

  //@ts-ignore
  const requests = blocks.map((block) => {
    const { accessToken } = nookies.get(null, 'accessToken');
    const finalParams = setParams(null, accessToken);
    finalParams.query = `${type} {
                          ${block.join('\n')}
                          }`;

    //@ts-ignore - TODO check axios types
    return axios({
      method: 'post',
      //@ts-ignore
      url: `${Config.api.uri[getNodeEnv()]}`,
      header: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      data: finalParams
    }).then(response => response.data).catch((error) => {
      console.log('post error', error, finalParams);

      if (error.response) {
        throw error.response.data;
      }
    });
  });

  return Promise.all(requests).catch(err => console.log(err));
};

/**
 * @param {*} params
 * @param {*} onUploadProgress
 */
export const upload = (params = {}, onUploadProgress) => {
  const { accessToken } = nookies.get(null, 'accessToken');
  const finalParams = (params === false) ? null : { params: setParams(params, accessToken) };
  const data = new FormData();
  Object.keys(finalParams?.params).forEach((param) => {
    if (Array.isArray(finalParams?.params[param])) {
      finalParams?.params[param].forEach(/** @param {any} item @param {number} i */(item, i) => {
        data.append(`${params}[${i}]`, item);
      });
    } else {
      data.append(param, finalParams?.params[param]);
    }
  });

  return axios.request({
    method: 'post',
    url: `${Config.webUploadApi.uri[getNodeEnv()]}/graphql?target=media`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data,
    onUploadProgress
  }).then(response => response.data).catch((error) => {
    console.log('post error', error);

    if (error.response) {
      throw error.response.data;
    }
  });
};

/**
 * @param {number} a
 * @returns {number}
 */
export const round = a => Math.round((a + Number.EPSILON) * 100) / 100;

/**
 * @param {string} email
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  if (email.includes('..') || !email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/)) return false;
  return true;
};

/**
 * @param {string} firstNineDigits
 * @returns {number}
 */
const calcFirstChecker = (firstNineDigits) => {
  /** @type {number?} */
  let s = null;

  for (let j = 0; j < 9; ++j) {
    // @ts-ignore - TODO fix s can be null
    s += Number(firstNineDigits.toString().charAt(j)) * (10 - j);
  }

  // @ts-ignore - TODO fix s can be null
  const lastSumChecker1 = s % 11;
  const checker1 = lastSumChecker1 < 2 ? 0 : 11 - lastSumChecker1;

  return checker1;
};

/**
 * @param {string} cpfWithChecker1
 * @returns {number}
 */
const calcSecondChecker = (cpfWithChecker1) => {
  let s = null;

  for (let k = 0; k < 10; ++k) {
    // @ts-ignore - TODO fix s can be null
    s += Number(cpfWithChecker1.toString().charAt(k)) * (11 - k);
  }

  // @ts-ignore - TODO fix s can be null
  const lastSumChecker2 = s % 11;
  const checker2 = lastSumChecker2 < 2 ? 0 : 11 - lastSumChecker2;

  return checker2;
};

/**
 * @param {string} cpf
 * @returns {boolean}
 */
export const validateCPF = (cpf) => {
  if (typeof cpf !== 'string' && typeof cpf !== 'number') {
    console.warn('Unsupported value');
    return false;
  }

  const cleanCPF = String(cpf).replace(/\.|-|\s/g, '');
  const firstNineDigits = cleanCPF.substring(0, 9);
  const checker = cleanCPF.substring(9, 11);

  if (cleanCPF.length !== 11) {
    return false;
  }

  // Check if all digits are equal
  for (let i = 0; i < 10; i++) {
    if (`${firstNineDigits}${checker}` === Array(12).join(String(i))) {
      return false;
    }
  }

  const checker1 = calcFirstChecker(firstNineDigits);
  const checker2 = calcSecondChecker(`${firstNineDigits}${checker1}`);

  return checker.toString() === checker1.toString() + checker2.toString();
};

/**
 * @see {@link https://github.com/fnando/cnpj}
 * @param {string} cnpj
 * @returns {number}
 */
const checkCnpjNumbers = (cnpj) => {
  let index = 2;
  /** @type {number[] | string} */
  const reverse = cnpj.split('')
    // @ts-ignore fix return
    .reduce((buffer, number) => [parseInt(number, 10)].concat(buffer), []);

  // @ts-ignore fix can be string type
  const s = reverse.reduce((buffer, number) => {
    buffer += number * index;
    index = index === 9 ? 2 : index + 1;
    return buffer;
  }, 0);

  const mod = s % 11;
  return mod < 2 ? 0 : 11 - mod;
};

/**
 * @param {string} cnpj
 * @returns {string | boolean}
 */
export const validateCNPJ = (cnpj) => {
  let cleanCNPJ = cnpj.replace(/\.|-|\s/g, '');
  cleanCNPJ = cleanCNPJ.replace(/[^\d]+/g, '');

  if (cleanCNPJ == '00000000000000'
    || cleanCNPJ == '11111111111111'
    || cleanCNPJ == '22222222222222'
    || cleanCNPJ == '33333333333333'
    || cleanCNPJ == '44444444444444'
    || cleanCNPJ == '55555555555555'
    || cleanCNPJ == '66666666666666'
    || cleanCNPJ == '77777777777777'
    || cleanCNPJ == '88888888888888'
    || cleanCNPJ == '99999999999999') { return false; }

  if (cleanCNPJ.length !== 14) return false;

  let numbers = cleanCNPJ.substring(0, 12);
  numbers += checkCnpjNumbers(numbers);
  numbers += checkCnpjNumbers(numbers);

  return numbers.substring(-2) === cleanCNPJ.substring(-2);
};

/**
 * @param {string} document
 * @returns {boolean}
 */
export const isDocumentCNPJ = (document) => {
  if (!document) return false;
  let cleanCNPJ = document.replace(/\.|-|\s/g, '');
  cleanCNPJ = cleanCNPJ.replace(/[^\d]+/g, '');
  return cleanCNPJ.length === 14;
};

/**
 * @param {string | number} organizationId
 * @returns
 */
export const getAllowedEntityId = async (organizationId) => {
  const response = await getApolloClient(null, {
    Authorization: `Bearer ${getCookie(null, 'accessToken')}`
  }).query({
    query: gql`query KrabsUserEntities($organizationId:ID){
        viewer {
          entities (limit: 1, type:[ADMIN,STAFF], organizationId:$organizationId) {
            nodes {
              id: dbId
              type
              organizationId
            }
          }
        }
      }`,
    variables: {
      organizationId
    }
  });
  const { entities } = response?.data?.viewer;

  if (!entities?.nodes.length) return null;

  return entities?.nodes[0].id;
};

/**
 * @param {string} cpf
 * @returns {string}
 */
export const formatCPF = (cpf) => cpf
  .replace(/\D/g, '')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d{1,2})/, '$1-$2')
  .replace(/(-\d{2})\d+?$/, '$1');

/**
 * @param {string} cnpj
 * @returns {string}
 */
export const formatCNPJ = (cnpj) => cnpj
  .replace(/\D/g, '')
  .replace(/(\d{2})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1.$2')
  .replace(/(\d{3})(\d)/, '$1/$2')
  .replace(/(\d{4})(\d{1,2})/, '$1-$2')
  .replace(/(-\d{2})\d+?$/, '$1');

/**
 * @param {(args: any[]) => void} callback
 * @returns {(args: any[]) => void}
 */
export const debounce = (callback, delay = 300) => {
  /** @type {NodeJS.Timeout} */
  let timer;

  return (...args) => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

/**
 * getNameOfMessageStatus - Get message status name given the message status number
 * @param {Number} messageStatus
 * @returns {string?}
 */
export const getNameOfMessageStatus = (messageStatus) => {
  switch (messageStatus) {
    case 0:
      return __('Enviado');
    case 1:
      return __('Recebido');
    case 2:
      return __('Lido');
    default:
      console.warn('Message status with code' + messageStatus + 'does not exists. function getNameOfMessageStatus is returning NULL');
      return null;
  }
};

/**
 * @param {boolean} [isClassAppLifetime]
 * @returns {number} Time in seconds
 */
export const getAccessTokenLifetime = (isClassAppLifetime) => {
  if (isClassAppLifetime) return 60 * 60 * 24 * 7; // 7 days
  if (process.env.NODE_ENV === 'development') return 60 * 60 * 10; // 10 hours
  return 60 * 30; // 30 minutes
};

/**
 * TODO Check all files to call this method when necessary
 * @param {number} amount
 * @param {AmountType} type
 * @param {number} [baseAmount]
 * @returns {number}
 */
export const getValueFromType = (amount, type, baseAmount) => {
  if (type === 'PERCENTAGE' && baseAmount) return amount * baseAmount;
  return amount;
};

/**
 * @param {number} number
 * @param {number} width
 * @returns {string}
 */
const zeroFill = (number, width) => {
  let countWidth = width;
  countWidth -= number.toString().length;
  if (countWidth > 0) {
    return new Array(countWidth + (/\./.test(number + '') ? 2 : 1)).join('0') + number;
  }
  return number + ''; // always return a string
};

/**
 * @typedef {{
 *  ispb: string,
 *  name: string,
 *  code: number,
 *  fullName: string,
 * }} BrasilApiBank
 *
 * @returns {Promise<(BrasilApiBank & {
 *  label: string,
 *  value: string,
 * })[]>}
 */
const getBrazilianBanks = async () => {
  /**
   * @type {import('axios').AxiosResponse<BrasilApiBank[]> | void}
   */
  const apiBanks = await axios({
    method: 'get',
    url: 'https://brasilapi.com.br/api/banks/v1',
  }).catch(e => console.error(e));

  return apiBanks?.data?.filter(bank => !!bank.code).map(bank => ({
    ...bank,
    label: zeroFill(bank.code, 3),
    value: `${zeroFill(bank.code, 3)} - ${bank.name}`
  })) || [];
};

/**
 * @typedef {{
 *  key: number,
 *  text: string,
 *  value: number,
 *  code: number,
 *  'data-id': number,
 * }} BankData
 *
 * @returns {Promise<BankData[]>}
 */
export const getBanks = async () => {
  let apiBanks = banks;

  try {
    apiBanks = await getBrazilianBanks();
  } catch (err) {
    console.error('Failed fetching banks from Brasil API, getting banks from local file instead. Error:', err);
  }

  apiBanks.sort((a, b) => a.code - b.code);

  return apiBanks.map((bank, key) => ({
    key,
    text: bank.value,
    value: bank.code,
    code: bank.code,
    'data-id': bank.code
  }));
};

/**
 * @param {FetchedOrganizationFromUser['permissions']} permissions
 * @returns {PermissionType}
 */
const getPermissionType = (permissions) => {
  if (permissions.hasAdmin) {
    return 'ADMIN';
  }
  if (permissions.entityScopes.includes('CREATE_UPDATE_RECURRENCE')
    && permissions.entityScopes.includes('DELETE_RECURRENCE')) {
    return 'EDIT';
  }
  return 'VIEW';
};

/**
 * @param {string} accessToken
 * @param {*} [ctx]
 * @returns {Promise<import('./store').User | null>}
 */
export const getUserData = async (accessToken, ctx) => {
  /**
   * @type {{
   *  id: number
   *  fullname: string
   *  phone: string
   *  email: string
   *  organizations: {
   *    nodes: FetchedOrganizationFromUser[]
   *  }
   *  acceptedTerms: {
   *    nodes: {
   *      id: number
   *      term: { id: number, name: string }
   *    }[]
   *  }
   * }}
   */
  const joyUser = await getApolloClient(null, {
    Authorization: `Bearer ${accessToken}`
  }).query({
    query: gql`query KrabsUserQuery {
      viewer {
        fullname
        phone
        email
        organizations(limit: 1000) {
          nodes{
            id: dbId
            fullname
            taxId
            confRecurrence
            paymentMethods { name }
            type
            logo { uri }
            permissions {
              hasAdmin
              entityScopes
            }
            recipientTags { totalCount }
            accounts (pendingCreation: true) { totalCount }
            myEntities (type: [ADMIN, STAFF]) { totalCount }
            collectionRule { totalCount }
          }
        }
        acceptedTerms {
          nodes {
            id
            term {
              id: dbId
              name
            }
          }
        }
      }
    }`
  }).then(result => result.data.viewer).catch((e) => console.error(e));

  /** @type {number} */
  let selectedOrganizationId = 0;
  /** @type {import('../components/SideMenu').Organization[]} */
  let organizations = [];
  /** @type {import('../components/SideMenu').Organization[]} */
  let organizationsToContract = [];
  let acceptedTerms = false;
  let notAuthorized = false;

  if (joyUser) {
    if (joyUser.organizations?.nodes?.length) {
      acceptedTerms = joyUser.acceptedTerms.nodes.some((term) => term.term.id === 9);
      organizations = joyUser.organizations.nodes.filter(({ permissions, confRecurrence }) => {
        if (!confRecurrence) return false;
        if (permissions.hasAdmin) return true;

        return permissions.entityScopes.includes('READ_ENTITY')
          && permissions.entityScopes.includes('READ_GROUP')
          && permissions.entityScopes.includes('READ_RECURRENCE');
      }).map(({ permissions, ...organization }) => ({
        ...organization,
        accessType: getPermissionType(permissions),
        entityScopes: permissions.entityScopes
      }));

      organizationsToContract = joyUser.organizations.nodes
        .filter(({ permissions, confRecurrence }) => !confRecurrence && permissions.hasAdmin)
        .map(({ permissions, ...organization }) => ({
          ...organization,
          accessType: 'ADMIN',
          entityScopes: permissions.entityScopes
        }));
    }

    if (organizations.length > 0) {
      const idFromCookie = getCookie(ctx, 'selectedOrganizationId');

      if (idFromCookie) selectedOrganizationId = +idFromCookie;
      if (!selectedOrganizationId || organizations.every(organization => +organization.id !== selectedOrganizationId)) {
        const contractedOrganizations = organizations.filter(org => org.confRecurrence);

        if (contractedOrganizations.length) {
          selectedOrganizationId = +organizations[0].id;
          setCookie(ctx, 'selectedOrganizationId', selectedOrganizationId + '');
        } else {
          destroyCookie(ctx, 'selectedOrganizationId');
        }
      }

      organizations = organizations.map((organization) => {
        let org = { ...organization };
        if (organization.paymentMethods) Object.assign(org,
          {
            paymentMethods: organization.paymentMethods.map(
              // @ts-ignore TODO query receive paymentMethods as {name: string, __typename: string}
              i => i.name)
          });
        return org;
      });
    } else if (!organizationsToContract.length) {
      notAuthorized = !joyUser.organizations?.nodes?.some(({ myEntities }) => myEntities.totalCount);
    }

    return {
      accessToken,
      fullname: joyUser.fullname,
      organizations,
      selectedOrganizationId,
      prevSelectedOrganizationId: 0,
      organizationsToContract,
      acceptedTerms,
      notAuthorized,
    };
  }

  return null;
};

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 * @returns {number}
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text, font) {
  // re-use canvas object for better performance
  // @ts-ignore
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));

  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(text);
  return metrics.width;
};
